import { useRef, useCallback } from "react";
import { ALLOWED_SERIES, type CardInput } from "../../odp/types.js";
import { addCardToOdp } from "../../odp/card-builder.js";
import { renderCardPreview, createImageUrls, revokeImageUrls } from "../../odp/card-preview.js";
import { exportPdfDirect } from "../../odp/card-pdf.js";
import { taggedFilename } from "../../overlay/naming.js";
import type { OdpTabState, OdpAction } from "../../hooks/useOdpReducer.js";

interface OdpCardTabProps {
  state: OdpTabState;
  dispatch: React.Dispatch<OdpAction>;
}

export function OdpCardTab({ state, dispatch }: OdpCardTabProps) {
  const odpFileRef = useRef<HTMLInputElement>(null);
  const imagesRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  const getCurrentCard = useCallback((): CardInput | null => {
    const images = imagesRef.current?.files?.length
      ? Array.from(imagesRef.current.files)
      : state.currentCard.images;
    const card = { ...state.currentCard, images };
    if (!card.title || images.length === 0) {
      dispatch({ type: "SET_MESSAGE", message: "請先填寫標題和選取圖片", messageType: "error" });
      return null;
    }
    return card;
  }, [state.currentCard, dispatch]);

  const handleAddCard = useCallback(async () => {
    const title = state.currentCard.title.trim();
    const description = state.currentCard.description.trim();
    const images = imagesRef.current?.files?.length
      ? Array.from(imagesRef.current.files)
      : state.currentCard.images;

    if (!title) { dispatch({ type: "SET_MESSAGE", message: "請填寫標題", messageType: "error" }); return; }
    if (!description) { dispatch({ type: "SET_MESSAGE", message: "請填寫說明", messageType: "error" }); return; }
    if (images.length === 0) { dispatch({ type: "SET_MESSAGE", message: "請選取至少 1 張圖片", messageType: "error" }); return; }
    if (images.length > 6) { dispatch({ type: "SET_MESSAGE", message: "最多 6 張圖片", messageType: "error" }); return; }

    const card: CardInput = {
      series: state.currentCard.series,
      title,
      techLabel: state.currentCard.techLabel,
      spec: state.currentCard.spec,
      topic: state.currentCard.topic,
      description,
      images,
    };

    dispatch({ type: "SET_PROCESSING", processing: true });
    dispatch({ type: "SET_MESSAGE", message: "處理中…", messageType: "info" });

    try {
      const sourceBlob = state.resultBlob || state.odpFile || null;
      const { blob, result } = await addCardToOdp(sourceBlob, card);
      const zoneLabel = result.zone === "left" ? "左" : "右";
      const newLabel = result.wasNewPage ? "（新頁面）" : "";
      const name = taggedFilename(state.odpFileName || "新規劃.odp", "card");
      dispatch({
        type: "SET_RESULT",
        blob,
        name,
        message: `Card 已新增到第 ${result.pageIndex + 1} 頁${zoneLabel}側${newLabel}`,
      });
    } catch (err) {
      dispatch({ type: "SET_PROCESSING", processing: false });
      dispatch({ type: "SET_MESSAGE", message: `失敗: ${(err as Error).message}`, messageType: "error" });
    }
  }, [state, dispatch]);

  const handlePreview = useCallback(() => {
    const card = getCurrentCard();
    if (!card || !previewRef.current) return;
    const urls = createImageUrls(card.images);
    renderCardPreview(previewRef.current, card, "left", urls, 0.55);
  }, [getCurrentCard]);

  const handlePdfExport = useCallback(async () => {
    const card = getCurrentCard();
    if (!card) return;
    dispatch({ type: "SET_MESSAGE", message: "產生 PDF 中…", messageType: "info" });
    try {
      const urls = createImageUrls(card.images);
      const blob = await exportPdfDirect([{ card, zone: "left", imageUrls: urls }]);
      revokeImageUrls(urls);
      const pdfName = taggedFilename(
        (state.odpFileName || "card.odp").replace(/\.odp$/i, ".pdf"),
        "card",
      );
      triggerDownload(blob, pdfName);
      dispatch({ type: "SET_MESSAGE", message: "PDF 已下載", messageType: "success" });
    } catch (err) {
      dispatch({ type: "SET_MESSAGE", message: `PDF 失敗: ${(err as Error).message}`, messageType: "error" });
    }
  }, [getCurrentCard, state.odpFileName, dispatch]);

  const handleOdpFileDrop = useCallback(
    (f: File) => {
      if (f.name.endsWith(".odp")) {
        dispatch({ type: "SET_FILE", file: f, name: f.name });
      } else {
        dispatch({ type: "SET_MESSAGE", message: "請上傳 .odp 檔案", messageType: "error" });
      }
    },
    [dispatch],
  );

  return (
    <div className="odp-tab">
      {state.message && (
        <div className={`msg msg-${state.messageType}`}>{state.message}</div>
      )}

      {/* 1. ODP Upload */}
      <div className="odp-section">
        <h3>1. 上傳 ODP 檔案</h3>
        <div
          className="odp-upload-zone"
          onClick={() => {
            if (!state.odpFile) odpFileRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            (e.currentTarget as HTMLElement).classList.add("dragover");
          }}
          onDragLeave={(e) => (e.currentTarget as HTMLElement).classList.remove("dragover")}
          onDrop={(e) => {
            e.preventDefault();
            (e.currentTarget as HTMLElement).classList.remove("dragover");
            const f = e.dataTransfer?.files[0];
            if (f) handleOdpFileDrop(f);
          }}
        >
          <input
            ref={odpFileRef}
            type="file"
            accept=".odp"
            style={{ display: "none" }}
            onChange={() => {
              const f = odpFileRef.current?.files?.[0];
              if (f) dispatch({ type: "SET_FILE", file: f, name: f.name });
            }}
          />
          {state.odpFile ? (
            <>
              <span className="odp-file-name">📄 {state.odpFileName}</span>
              <button
                className="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch({ type: "CLEAR_FILE" });
                }}
              >
                清除
              </button>
            </>
          ) : (
            <span className="odp-upload-label">拖曳 .odp 檔案至此，或點擊選取</span>
          )}
        </div>
      </div>

      {/* 2. Card Form */}
      <div className="odp-section">
        <h3>2. 填寫 Card 資料</h3>
        <div className="odp-form">
          <label>
            系列
            <select
              value={state.currentCard.series}
              onChange={(e) => dispatch({ type: "SET_CARD_FIELD", field: { series: e.target.value } })}
            >
              {ALLOWED_SERIES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>

          <label>
            藍色 Pill 標題 *
            <input
              type="text"
              value={state.currentCard.title}
              placeholder="例：未來生活解密：AI 驅動的綠能革命"
              onChange={(e) => dispatch({ type: "SET_CARD_FIELD", field: { title: e.target.value } })}
            />
          </label>

          <label>
            橘色 Pill（可選）
            <input
              type="text"
              value={state.currentCard.techLabel ?? ""}
              placeholder="例：關鍵技術：智慧電網 × 儲能系統"
              onChange={(e) =>
                dispatch({ type: "SET_CARD_FIELD", field: { techLabel: e.target.value || undefined } })
              }
            />
          </label>

          <label>
            規格
            <input
              type="text"
              value={state.currentCard.spec}
              onChange={(e) => dispatch({ type: "SET_CARD_FIELD", field: { spec: e.target.value } })}
            />
          </label>

          <label>
            題目（可選）
            <input
              type="text"
              value={state.currentCard.topic ?? ""}
              placeholder="例：當 AI 遇上綠電"
              onChange={(e) =>
                dispatch({ type: "SET_CARD_FIELD", field: { topic: e.target.value || undefined } })
              }
            />
          </label>

          <label>
            說明（「」內自動粗體）*
            <textarea
              rows={3}
              value={state.currentCard.description}
              placeholder="例：6/20發文，以「AI 賦能」為切角帶出「智慧電網」。"
              onChange={(e) =>
                dispatch({ type: "SET_CARD_FIELD", field: { description: e.target.value } })
              }
            />
          </label>

          <label>
            圖片（1-6 張）*
            <input
              ref={imagesRef}
              type="file"
              accept="image/*"
              multiple
              onChange={() => {
                const files = imagesRef.current?.files
                  ? Array.from(imagesRef.current.files)
                  : [];
                dispatch({ type: "SET_CARD_FIELD", field: { images: files } });
              }}
            />
          </label>
          {state.currentCard.images.length > 0 && (
            <div className="odp-img-count">{state.currentCard.images.length} 張圖片已選取</div>
          )}
        </div>
      </div>

      {/* 3. Actions */}
      <div className="odp-section">
        <h3>3. 執行</h3>
        <div className="odp-actions">
          <button
            className="primary"
            disabled={state.processing}
            onClick={() => void handleAddCard()}
          >
            {state.processing
              ? "處理中…"
              : state.odpFile
                ? "新增 Card 到 ODP"
                : "建立新 ODP + Card"}
          </button>
        </div>
      </div>

      {/* 4. Preview */}
      <div className="odp-section">
        <h3>4. 預覽</h3>
        <div className="odp-actions" style={{ marginBottom: "0.75rem" }}>
          <button className="secondary" onClick={handlePreview}>
            👁️ 產生預覽
          </button>
          <button className="secondary" onClick={() => void handlePdfExport()}>
            📄 匯出 PDF（向量）
          </button>
        </div>
        <div ref={previewRef} />
      </div>

      {/* 5. Download */}
      {state.resultBlob && (
        <div className="odp-section">
          <h3>5. 下載 ODP</h3>
          <div className="odp-actions">
            <button
              className="primary"
              onClick={() => triggerDownload(state.resultBlob!, state.resultName)}
            >
              📥 下載 {state.resultName}
            </button>
            <span className="odp-result-size">
              {(state.resultBlob.size / 1024 / 1024).toFixed(1)} MB
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
